import { useCallback, useEffect, useState } from 'react';
import {
  api,
  saveFile,
  type Company,
  type Invoice,
  type Payment,
  type Settlement,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as money, date as written } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';

/**
 * Jedna faktura z bliska: dokument, zapłaty i notatka (E4, E10).
 *
 * Do tej pory faktura żyła wyłącznie jako wiersz listy. Wiersz odpowiada na pytanie „ile ich jest",
 * ale nie na „czy ten klient zapłacił" i nie na „co ustaliliśmy w sprawie tej faktury" — a to są
 * pytania, które zadaje się przy konkretnym dokumencie. Stąd ten ekran, i stąd trzy rzeczy na nim:
 *
 * 1. **Podgląd dokumentu** — ten sam PDF, który dostał nabywca. Bez niego trzeba było pobierać plik,
 *    żeby sprawdzić, co właściwie wystawiono.
 * 2. **Zapłaty, także częściowe.** Wpłata mniejsza niż kwota faktury rozlicza tyle, ile wpłynęło,
 *    a reszta zostaje w rozrachunkach — bo tak wyglądają zapłaty w rzeczywistości.
 * 3. **Notatka wewnętrzna**, której nie ma ani na wydruku, ani w pliku KSeF. Dlatego wolno ją
 *    dopisać także do faktury wystawionej, kiedy treści dokumentu zmieniać już nie można.
 */
export function InvoiceDetail({
  company,
  invoice,
  settlement,
  editable,
  t,
  locale,
  onClose,
  onChanged,
  onEditDraft,
  onCorrect,
}: {
  company: Company;
  invoice: Invoice;
  /** Stan rozrachunku, gdy faktura jest w należnościach; liczby stamtąd uwzględniają korekty. */
  settlement: Settlement | undefined;
  editable: boolean;
  t: Dictionary;
  locale: Locale;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onEditDraft: (id: string) => void;
  onCorrect: (invoice: Invoice) => void;
}) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [note, setNote] = useState(invoice.internalNote ?? '');
  const [noteSaved, setNoteSaved] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [form, setForm] = useState({ paidOn: today(), amount: '', exchangeRate: '', method: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const issued = invoice.status === 'ISSUED';
  /** Faktura w walucie obcej: wpłata potrzebuje kursu z dnia zapłaty, inaczej nie wejdzie do ksiąg. */
  const foreign = invoice.currency !== 'PLN';

  const load = useCallback(async () => {
    if (!issued) return;
    try {
      setPayments(await api.invoicePayments(company.id, invoice.id));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id, invoice.id, issued]);

  useEffect(() => {
    void load();
  }, [load]);

  // Podgląd bierzemy raz i trzymamy w pamięci karty; adres zwalniamy przy zamknięciu, żeby
  // przejrzenie dziesięciu faktur po kolei nie zostawiło dziesięciu kopii pliku
  useEffect(() => {
    if (!issued) return undefined;
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const file = await api.invoicePdf(company.id, invoice.id);
        objectUrl = URL.createObjectURL(file.blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPdfUrl(objectUrl);
      } catch (failure) {
        if (!cancelled) setError(failureOf(failure));
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [company.id, invoice.id, issued]);

  const paidFromPayments = payments.reduce((sum, payment) => sum + cents(payment.amount), 0);
  const due = settlement ? cents(settlement.amountDue) : cents(invoice.totalGross);
  const paid = settlement ? cents(settlement.amountPaid) : paidFromPayments;
  const remaining = settlement ? cents(settlement.remaining) : due - paid;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      await onChanged();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {invoice.number ?? t.invoices.statuses[invoice.status]}
            {invoice.documentKind === 'CORRECTION' && <span className="muted sm"> · {t.invoices.correction}</span>}
          </h3>
          <p>
            {written(invoice.issueDate, locale)} · {t.invoices.gross}: {money(invoice.totalGross, locale)}{' '}
            {invoice.currency}
          </p>
        </div>
        <div className="sp">
          <button type="button" className="btn ghost" onClick={onClose}>
            {t.invoices.backToList}
          </button>
        </div>
      </div>

      <ErrorAlert error={error} t={t} />

      <div className="panel">
        <div className="row">
          <span className={issued ? 'chip st-info' : 'chip st-draft'}>
            <i />
            {t.invoices.statuses[invoice.status]}
          </span>
          {settlement && (
            <span className={settlement.daysOverdue > 0 ? 'chip st-bad' : 'chip st-warn'}>
              <i />
              {settlement.daysOverdue > 0
                ? `${t.settlements.overdueDays} ${settlement.daysOverdue}`
                : t.settlements.statuses[settlement.status]}
            </span>
          )}
        </div>

        <div className="tbl">
          <table>
            <tbody>
              <tr>
                <td>{t.invoices.counterparty}</td>
                <td>{settlement?.counterpartyName ?? '—'}</td>
              </tr>
              <tr>
                <td>{t.invoices.saleDate}</td>
                <td className="doc">{written(invoice.saleDate, locale)}</td>
              </tr>
              <tr>
                <td>{t.invoices.dueDate}</td>
                <td className="doc">{invoice.dueDate ? written(invoice.dueDate, locale) : '—'}</td>
              </tr>
              <tr>
                <td>{t.invoices.net}</td>
                <td className="n mono">{money(invoice.totalNet, locale)}</td>
              </tr>
              <tr>
                <td>{t.invoices.vat}</td>
                <td className="n mono">{money(invoice.totalVat, locale)}</td>
              </tr>
              <tr>
                <td>{t.invoices.gross}</td>
                <td className="n mono">{money(invoice.totalGross, locale)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Czynności faktury stoją tutaj, a nie w wierszu listy: w wierszu rozpychały tabelę
            tak, że kwoty uciekały poza ekran */}
        <div className="bar2">
          <div className="sp">
            {issued && (
              <>
                <button
                  type="button"
                  className="btn sec"
                  disabled={busy}
                  onClick={() => void run(async () => saveFile(await api.invoicePdf(company.id, invoice.id)))}
                >
                  <Icon.Download />
                  {t.invoices.detail.pdf}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={busy}
                  onClick={() => void run(async () => saveFile(await api.invoiceKsefFile(company.id, invoice.id)))}
                >
                  <Icon.Download />
                  {t.invoices.ksefFile}
                </button>
              </>
            )}
            {issued && invoice.documentKind === 'INVOICE' && editable && (
              <button type="button" className="btn ghost" disabled={busy} onClick={() => onCorrect(invoice)}>
                {t.invoices.correct}
              </button>
            )}
            {!issued && editable && (
              <>
                <button type="button" className="btn ghost" disabled={busy} onClick={() => onEditDraft(invoice.id)}>
                  {t.invoices.editDraft}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void run(() => api.issueInvoice(company.id, invoice.id))}
                >
                  {t.invoices.issue}
                </button>
                <button
                  type="button"
                  className="btn danger"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await api.deleteInvoice(company.id, invoice.id);
                      onClose();
                    })
                  }
                >
                  {t.invoices.delete}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {issued && (
        <div className="panel">
          <header>
            <h4>{t.invoices.detail.payments}</h4>
            <div className="sp">
              <span className="muted sm">
                {t.invoices.detail.due}: <span className="mono">{money(fromCents(due), locale)}</span> ·{' '}
                {t.invoices.detail.paid}: <span className="mono">{money(fromCents(paid), locale)}</span> ·{' '}
                {t.invoices.detail.remaining}: <span className="mono">{money(fromCents(remaining), locale)}</span>
              </span>
            </div>
          </header>

          {payments.length === 0 ? (
            <p className="hint">{t.invoices.detail.noPayments}</p>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.invoices.detail.paidOn}</th>
                    <th className="n">{t.invoices.detail.amount}</th>
                    <th>{t.invoices.detail.method}</th>
                    <th>{t.invoices.detail.note}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="doc">{written(payment.paidOn, locale)}</td>
                      <td className="n mono">{money(payment.amount, locale)}</td>
                      <td className="muted sm">{payment.method ?? '—'}</td>
                      <td className="muted sm">{payment.note ?? '—'}</td>
                      <td className="act">
                        {editable && (
                          <button
                            type="button"
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => {
                              if (!window.confirm(t.invoices.detail.confirmDeletePayment(payment.amount))) return;
                              void run(() => api.deletePayment(company.id, payment.id));
                            }}
                          >
                            {t.invoices.detail.deletePayment}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editable && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  await api.payInvoice(company.id, invoice.id, {
                    paidOn: form.paidOn,
                    amount: form.amount.replace(',', '.'),
                    // Kurs wysyłamy tylko przy walucie obcej — przy złotówkach serwer go nie pyta
                    exchangeRate: foreign ? form.exchangeRate.replace(',', '.') || null : null,
                    method: form.method === '' ? null : form.method,
                    note: form.note === '' ? null : form.note,
                  });
                  setForm({ paidOn: today(), amount: '', exchangeRate: '', method: '', note: '' });
                });
              }}
            >
              <div className="fields">
                <div className="field">
                  <label htmlFor="paidOn">{t.invoices.detail.paidOn}</label>
                  <input
                    id="paidOn"
                    className="inp"
                    type="date"
                    value={form.paidOn}
                    onChange={(event) => setForm({ ...form, paidOn: event.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="paymentAmount">{t.invoices.detail.amount}</label>
                  <input
                    id="paymentAmount"
                    className="inp mono"
                    inputMode="decimal"
                    // Podpowiadamy kwotę pozostałą, ale jej nie narzucamy: wpłata częściowa
                    // jest tu zwykłym przypadkiem, a nie wyjątkiem do obsłużenia gdzie indziej
                    placeholder={fromCents(remaining)}
                    value={form.amount}
                    onChange={(event) => setForm({ ...form, amount: event.target.value })}
                    required
                  />
                </div>
                {foreign && (
                  <div className="field">
                    <label htmlFor="paymentRate">{t.invoices.detail.exchangeRate}</label>
                    <input
                      id="paymentRate"
                      className="inp mono"
                      inputMode="decimal"
                      value={form.exchangeRate}
                      onChange={(event) => setForm({ ...form, exchangeRate: event.target.value })}
                      required
                    />
                    <span className="hint">{t.invoices.detail.exchangeRateHint}</span>
                  </div>
                )}
                <div className="field">
                  <label htmlFor="paymentMethod">{t.invoices.detail.method}</label>
                  <input
                    id="paymentMethod"
                    className="inp"
                    list="payment-methods"
                    value={form.method}
                    onChange={(event) => setForm({ ...form, method: event.target.value })}
                  />
                  <datalist id="payment-methods">
                    <option value={t.wizard.methods.transfer} />
                    <option value={t.wizard.methods.cash} />
                    <option value={t.wizard.methods.card} />
                  </datalist>
                </div>
                <div className="field">
                  <label htmlFor="paymentNote">{t.invoices.detail.note}</label>
                  <input
                    id="paymentNote"
                    className="inp"
                    value={form.note}
                    onChange={(event) => setForm({ ...form, note: event.target.value })}
                  />
                </div>
              </div>
              <div className="bar2">
                <button
                  type="button"
                  className="btn plain"
                  onClick={() => setForm({ ...form, amount: fromCents(remaining) })}
                >
                  {t.invoices.detail.remaining}: {money(fromCents(remaining), locale)}
                </button>
                <div className="sp">
                  <button type="submit" className="btn" disabled={busy || form.amount.trim() === ''}>
                    {t.invoices.detail.addPayment}
                  </button>
                </div>
              </div>
              <p className="hint">{t.invoices.detail.partialHint}</p>
            </form>
          )}
        </div>
      )}

      <div className="panel">
        <header>
          <h4>{t.invoices.detail.internalNote}</h4>
        </header>
        <p className="hint">{t.invoices.detail.internalNoteHint}</p>
        <div className="field">
          <label htmlFor="internalNote">{t.invoices.detail.internalNote}</label>
          <textarea
            id="internalNote"
            className="inp"
            rows={3}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setNoteSaved(false);
            }}
            disabled={!editable}
          />
        </div>
        {editable && (
          <div className="bar2">
            <button
              type="button"
              className="btn sec"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.setInvoiceNote(company.id, invoice.id, note.trim() === '' ? null : note);
                  setNoteSaved(true);
                })
              }
            >
              {t.invoices.detail.saveNote}
            </button>
            {noteSaved && (
              <span className="hint" role="status">
                {t.invoices.detail.noteSaved}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="panel">
        <header>
          <h4>{t.invoices.detail.preview}</h4>
          {pdfUrl && (
            <div className="sp">
              <a className="btn ghost sm" href={pdfUrl} target="_blank" rel="noopener noreferrer">
                {t.invoices.detail.previewOpen}
              </a>
            </div>
          )}
        </header>
        {!issued ? (
          <p className="hint">{t.invoices.detail.draftNoDocument}</p>
        ) : pdfUrl ? (
          <object
            data={pdfUrl}
            type="application/pdf"
            aria-label={invoice.number ?? t.invoices.detail.preview}
            style={{ width: '100%', height: '70vh' }}
          >
            <p className="hint">{t.invoices.detail.previewOpen}</p>
          </object>
        ) : (
          <p className="muted">{t.loading}</p>
        )}
      </div>
    </>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Kwoty liczymy w groszach: złotówki w liczbach zmiennoprzecinkowych potrafią dać 122,99999. */
function cents(value: string): number {
  return Math.round(Number(String(value).replace(',', '.')) * 100);
}

function fromCents(value: number): string {
  return (value / 100).toFixed(2);
}
